import $ = require("jquery");
import ol = require("ol");

import MapView = require("../views/MapView");
import Portal = require("portal-gis");
import Utils = require("../utils");
import Symbology = require("../utils/symbology");
import AgsLayers = require("../utils/ags/ags-layers");
import { ArcGisVectorSourceFactory } from "../../components/ol3-symbolizer/ol3-symbolizer/ags/ags-source";
import Mediator = require("../mediator");

class Handler {
  /**
   * Create a vector layer pre-populated with everything in this featureserver
   *
   * see also,
   *
   * ags-geoquery-locator: command wrapper to feature service reader
   * feature-service-reader: query a feature service
   * map-service-reader: ags query service helper
   * converter: converts between ags and ol3 encoding
   *
   * but in this case there is no user filter and
   * the results get added to the layer directly
   *
   * NOTE: there is no mechanism for advertising the layer options (e.g. maxQueryCount)
   */

  static about = `Create a vector layer pre-populated with everything in this featureserver`;

  static options = {
    layerType: {
      about: "Service type to query (only queries featureserver services).",
      domain: "featureserver",
      default: "featureserver",
    },
  };

  private static featureserver(l: Portal.MapLayerLeaf, view: MapView, mediator: Mediator) {
    if (!mediator) throw "cannot create feature server layer without a map";
    const d = $.Deferred<ol.layer.Base>();

    mediator.mapViewMap((map) => {
      const options = Utils.asDictionary(l.Options.Values);

      let where = options["where"] || "1=1";

      Utils.ifOption(options, "filter", (value) => {
        if (0 === value.indexOf("{")) {
          const filter = JSON.parse(value);
          where = Object.keys(filter)
            .map((k) => `${k}=${filter[k]}`)
            .join(" AND ");
        } else {
          where = value;
        }
      });

      const url = l.url;
      let layerId: string;
      let [services, serviceName] = url.split("/rest/services/", 2);
      let serviceType: "FeatureServer" | "MapServer" = "FeatureServer";
      if (-1 == serviceName.indexOf(serviceType)) serviceType = "MapServer";
      if (-1 == serviceName.indexOf(serviceType)) throw "unknown service type";
      [serviceName, layerId] = serviceName.split(`/${serviceType}/`, 2);

      ArcGisVectorSourceFactory.create({
        services: `${services}/rest/services`,
        serviceName: serviceName,
        serviceType: serviceType,
        layers: [parseInt(layerId)],
        where: where,
        maxQueryCount: parseInt(options["maxQueryCount"] || "1000"),
        uidFieldName: options["uidFieldName"] || "FID",
        outFields: options["outFields"] || "*",
        map: map,
        tileSize: 256,
      }).then((layers) => {
        const layer = layers[0];
        const symbology = options["symbology"];

        layer.set("id", l.id);
        layer.set("title", l.text);
        layer.setVisible("false" !== options["visible"]);

        if (symbology) {
          layer.set("symbology", symbology);
          view.mediator.mapViewMap((map) => {
            layer.setStyle((feature: ol.Feature, resolution: number) => {
              const attributes = feature.getProperties();
              attributes["symbology"] = symbology;
              attributes["zoom"] = map.getView().getZoom() + "";
              attributes["geometryType"] = feature.getGeometry().getType();

              return Symbology.findStyle(view.mediator, {
                attributes: attributes,
              });
            });
          });
        }

        AgsLayers.setMinMaxZoomLevel(view, layer, l);

        d.resolve(layer);
      });
    });

    return d;
  }

  static create(args: {
    layer: Portal.MapLayerLeaf;
    view: MapView;
    mediator: Mediator;
  }): ol.layer.Layer | JQueryDeferred<ol.layer.Layer> {
    if (!args.mediator) throw "mediator is required to acquire map";
    if (args.map) throw "stop providing the map...just give me a mediator";
    const l = args.layer;
    const options = Utils.asDictionary(l.Options.Values);
    const layerType = options["layerType"] || "featureserver";
    let factory = Handler.featureserver;
    factory = Handler[layerType] || factory;
    {
      // to be done server-side and generically across all attributes and options
      l.url = Utils.translate(l.url);
      // Utils.translate should be happening on the server side in the future
      // just being selective/restrictive/expedient for now
      l.Options.Values.forEach((v) => (v.value = Utils.translate(v.value)));
    }
    return factory && factory(l, args.view, args.mediator);
  }
}

export = Handler;
